import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';

interface ExternalAdmin {
  adminUserID: number;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  createdDate: string;
  hasPassword: boolean;
  mustChangePassword: boolean;
}

@Component({
  selector: 'app-external-admins',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './external-admins.component.html',
  styleUrls: ['./external-admins.component.scss']
})
export class ExternalAdminsComponent implements OnInit {
  admins: ExternalAdmin[] = [];
  loading = true;
  error = '';
  actionMessage = '';

  showCreateForm = false;
  creating = false;
  newEmail = '';
  newFirstName = '';
  newLastName = '';
  newPassword = '';
  newConfirmPassword = '';
  createError = '';

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadAdmins();
  }

  loadAdmins(): void {
    this.loading = true;
    this.error = '';

    this.apiService.getExternalAdmins().subscribe({
      next: (data) => {
        this.admins = data;
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load external admin accounts.';
        this.loading = false;
      }
    });
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.newEmail = '';
    this.newFirstName = '';
    this.newLastName = '';
    this.newPassword = '';
    this.newConfirmPassword = '';
    this.createError = '';
  }

  cancelCreate(): void {
    this.showCreateForm = false;
    this.createError = '';
  }

  createAdmin(): void {
    if (!this.newEmail) {
      this.createError = 'Email is required';
      return;
    }

    const hasPassword = !!this.newPassword;

    if (hasPassword) {
      if (this.newPassword.length < 8) {
        this.createError = 'Password must be at least 8 characters long';
        return;
      }
      if (this.newPassword !== this.newConfirmPassword) {
        this.createError = 'Passwords do not match';
        return;
      }
    }

    this.creating = true;
    this.createError = '';

    this.apiService.createExternalAdmin({
      email: this.newEmail,
      firstName: this.newFirstName || undefined,
      lastName: this.newLastName || undefined,
      password: this.newPassword || undefined
    }).subscribe({
      next: () => {
        this.creating = false;
        this.showCreateForm = false;
        this.actionMessage = hasPassword
          ? `Account created for ${this.newEmail}. They will be required to change their password on first login.`
          : `Account created. Setup email sent to ${this.newEmail}.`;
        this.loadAdmins();
        setTimeout(() => this.actionMessage = '', 6000);
      },
      error: (err) => {
        this.creating = false;
        this.createError = err.error?.error || 'Failed to create admin account.';
      }
    });
  }

  toggleActive(admin: ExternalAdmin): void {
    const action = admin.isActive
      ? this.apiService.deactivateExternalAdmin(admin.adminUserID)
      : this.apiService.activateExternalAdmin(admin.adminUserID);

    action.subscribe({
      next: () => {
        admin.isActive = !admin.isActive;
        this.actionMessage = `Account ${admin.isActive ? 'activated' : 'deactivated'}.`;
        setTimeout(() => this.actionMessage = '', 3000);
      },
      error: () => {
        this.actionMessage = 'Action failed. Please try again.';
        setTimeout(() => this.actionMessage = '', 3000);
      }
    });
  }

  deleteAdmin(admin: ExternalAdmin): void {
    if (!confirm(`Delete admin account for ${admin.email}? This cannot be undone.`)) return;

    this.apiService.deleteExternalAdmin(admin.adminUserID).subscribe({
      next: () => {
        this.admins = this.admins.filter(a => a.adminUserID !== admin.adminUserID);
        this.actionMessage = `Account for ${admin.email} deleted.`;
        setTimeout(() => this.actionMessage = '', 4000);
      },
      error: (err) => {
        this.actionMessage = err.error?.error || 'Failed to delete admin.';
        setTimeout(() => this.actionMessage = '', 4000);
      }
    });
  }

  resendSetupEmail(admin: ExternalAdmin): void {
    this.apiService.resendExternalAdminSetupEmail(admin.adminUserID).subscribe({
      next: () => {
        this.actionMessage = `Setup email resent to ${admin.email}.`;
        setTimeout(() => this.actionMessage = '', 4000);
      },
      error: (err) => {
        this.actionMessage = err.error?.error || 'Failed to resend email.';
        setTimeout(() => this.actionMessage = '', 4000);
      }
    });
  }

  displayName(admin: ExternalAdmin): string {
    const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ');
    return name || '—';
  }
}
